import React, { useState, useEffect, useContext } from "react";
import { Col, Row, List, Button, Image, message, Typography, Tag } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";

import {
  DateFormat,
  getApi,
  saveApi,
  successMessage,
} from "../../../../helpers";
import Sidebar from "../../../../components/Sidebar";
import CategoryPopup from "../../Category/Popup";
import UploadImage from "../../../../components/UploadImage";
import Popup from "../../../../components/Popup";
import { StoreContext } from "../../../../context";
import { canWrite } from "../../../../helpers/permissions";

const { Title, Text } = Typography;

export default function SubCategorySidebar({
  visible = false,
  data = {},
  onClose,
  onUploadDone,
  editCategory,
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});
  const [popup, setPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [deleteImagePopup, setDeleteImagePopup] = useState("");

  useEffect(() => getList(), []);

  const getList = async () => {
    setLoading(true);
    const res = await getApi(
      { sub_category_id: data.sub_category_id },
      "getSubCategoryGroup"
    );
    if (res.data && res.data.length) {
      res.data.shift();
      setList(res.data);
    }
    setLoading(false);
  };

  const saveHandler = async (values) => {
    setPopup(false);
    setLoading(true);
    let apiName = "createSubCategoryGroup";
    let msg = "";
    let payload = { name: values.name, description: values.description };
    
    if (selected.sub_category_group_id) {
      apiName = "updateSubCategoryGroup";
      payload = {
        ...payload,
        sub_category_group_id: selected.sub_category_group_id,
      };
    } else {
      payload = {
        ...payload,
        sub_category_id: data.sub_category_id,
        getId: true,
      };
    }
    
    const res = await saveApi(payload, msg, apiName);
    if (res.sub_category_group_id) {
      if (values.file?.file?.originFileObj) {
        const fmData = new FormData();
        const headers = { "content-type": "multipart/form-data" };
        fmData.append("sub_category_group_id", res.sub_category_group_id);
        fmData.append(
          "sub_category_group_img",
          values.file.file?.originFileObj
        );
        const re = await getApi(
          fmData,
          "uploadSubCategoryGroupImage",
          "ecomm/",
          headers
        );
        if (re.data) getList();
        else setLoading(false);
      } else getList();
    } else if (res) getList();
    else setLoading(false);
  };

  const deleteImageHandler = async () => {
    let payload = {
      sub_category_image_id: data.image_id,
    };
    let msg = "Sub Category Image Deleted";
    let apiName = "deleteSubCategoryImage";
    
    if (deleteImagePopup !== "SubCategory") {
      payload = { sub_category_group_image_id: deleteImagePopup };
      msg = "Group Image Deleted";
      apiName = "deleteSubCategoryGroupImage";
    }
    
    setDeleteImagePopup("");
    setLoading(true);
    const res = await saveApi(payload, msg, apiName);
    if (res) {
      deleteImagePopup === "SubCategory" ? onUploadDone() : getList();
    } else setLoading(false);
  };

  return (
    <>
      {deleteImagePopup && (
        <Popup
          saveText="Yes, Delete"
          title={
            deleteImagePopup === "SubCategory"
              ? "Delete Sub Category Image"
              : "Delete Group Image"
          }
          saveHandler={deleteImageHandler}
          visible
          cancelHandler={() => setDeleteImagePopup("")}
        >
          <Text strong style={{ fontSize: 18 }}>
            Are you sure to delete the Image ?
          </Text>
          <br />
          <Text type="danger">This action cannot be reverted.</Text>
        </Popup>
      )}
      {popup && (
        <CategoryPopup
          visible={popup}
          title={popupTitle}
          data={selected}
          uploadImage={true}
          isSubCategoryGroup={true}
          saveHandler={(values) => saveHandler(values)}
          cancelHandler={() => setPopup(false)}
        />
      )}
      <Sidebar
        title="Sub Category Details"
        visible={visible}
        loading={loading}
        onClose={onClose}
        footer={
          !storeId && canWrite() && (
            <div style={{
              display: 'flex',
              gap: '12px',
              padding: '16px 0'
            }}>
              <Button
                icon={<EditOutlined />}
                onClick={editCategory}
                type="primary"
                size="large"
                style={{
                  width: '100%',
                  height: '48px',
                  borderRadius: '8px'
                }}
              >
                Edit Sub Category Details
              </Button>
            </div>
          )
        }
      >
        <div style={{ padding: '0 4px' }}>
          {/* Image Section */}
          <div style={{ marginBottom: '16px', backgroundColor: '#F7F7F7', borderRadius: '12px', padding: '12px' }}>
            <div style={{ position: 'relative' }}>
              {data.sub_category_image ? (
                <Image
                  alt="subcategory img"
                  width="100%"
                  height={280}
                  style={{ 
                    borderRadius: '12px',
                    objectFit: 'cover'
                  }}
                  src={data.sub_category_image}
                />
              ) : null}
            </div>

            {/* Upload component */}
            {!storeId && (
              <div style={{ marginTop: '16px' }}>
                {data.sub_category_image ? (
                  <Button
                    onClick={() => setDeleteImagePopup("SubCategory")}
                    danger
                    style={{ borderRadius: '6px' }}
                  >
                    <DeleteOutlined /> Delete Image
                  </Button>
                ) : (
                  <UploadImage
                    onUploadDone={onUploadDone}
                    type_id={data.sub_category_id}
                    setLoading={setLoading}
                    title="Add Sub Category Image"
                    apiName="uploadSubCategoryImage"
                    type="sub_category"
                    style={{ 
                      borderRadius: '12px' 
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Title and Status */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: "flex", justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <Title style={{ 
                margin: 0, 
                fontSize: '24px',
                fontWeight: 600,
                lineHeight: 1.2,
                flex: 1
              }} level={3}>
                {data.sub_category_name}
              </Title>
              {/* <span
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: "4px 12px",
                  textTransform: "uppercase",
                  borderRadius: '6px',
                  marginLeft: '12px',
                  backgroundColor: '#f6ffed',
                  color: '#52c41a',
                  border: '1px solid #b7eb8f'
                }}
              >
                Active
              </span> */}
            </div>
            
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Created: {DateFormat(data.created_at)}
            </Text>
          </div>

          {/* Category */}
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontSize: '16px', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
              Category
            </Text>
            <span style={{
              fontSize: '14px',
              padding: '6px 12px',
              borderRadius: '6px',
              backgroundColor: 'var(--component-background, #fafafa)',
              border: '1px solid var(--border-color, #d9d9d9)',
              color: 'var(--text-color-secondary, #666)',
              display: 'inline-block'
            }}>
              {data.category_name || "N/A"}
            </span>
          </div>

          {/* Description */}
          {data.description && (
            <div style={{ marginBottom: '16px' }}>
              <Text style={{ fontSize: '16px', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
                Description
              </Text>
              <Text style={{ fontSize: '14px', color: '#666', lineHeight: 1.5 }}>
                {data.description}
              </Text>
            </div>
          )}

          {/* Groups List */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Text style={{ fontSize: '16px', fontWeight: 600 }}>
                Groups
              </Text>
              {!storeId && canWrite() && (
                <Button
                  onClick={() => (
                    setSelected({}),
                    setPopupTitle("Add Group"),
                    setPopup(true)
                  )}
                  type="primary"
                  icon={<PlusSquareOutlined />}
                  style={{ borderRadius: '6px' }}
                >
                  Add Group
                </Button>
              )}
            </div>
            
            <List
              bordered={false}
              size="small"
              itemLayout="horizontal"
              dataSource={list}
              renderItem={(group) => (
                <List.Item
                  style={{ 
                    padding: '12px',
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ width: '60px', height: '60px', borderRadius: '8px', overflow: 'hidden' }}>
                        {group.sub_category_group_image ? (
                          <Image
                            width={60}
                            height={60}
                            style={{ objectFit: "cover" }}
                            alt="group img"
                            src={group.sub_category_group_image}
                            preview={false}
                          />
                        ) : !storeId && (
                          <UploadImage
                            miniBtn
                            onUploadDone={() => getList()}
                            type_id={group.sub_category_group_id}
                            setLoading={setLoading}
                            title="Add Group Image"
                            apiName="uploadSubCategoryGroupImage"
                            type="sub_category_group"
                            fileList={
                              group.sub_category_group_image
                                ? [
                                    {
                                      url: group.sub_category_group_image,
                                      uid: "-1" + group.sub_category_group_id,
                                      status: "done",
                                      name: "image.png",
                                      image_id: group.image_id
                                        ? group.image_id.toString()
                                        : null,
                                    },
                                  ]
                                : []
                            }
                            onRemove={(image) =>
                              setDeleteImagePopup(image.image_id)
                            }
                            done={
                              group.sub_category_group_image || storeId
                                ? true
                                : false
                            }
                          />
                        )}
                      </div>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text strong style={{ fontSize: '16px' }}>
                          {group.sub_category_group_name}
                        </Text>
                        {!storeId && canWrite() && (
                          <Button
                            onClick={() => (
                              setPopupTitle("Edit Group Details"),
                              setSelected(group),
                              setPopup(true)
                            )}
                            type="link"
                            icon={<EditOutlined />}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    }
                    description={
                      <div>
                        {group.description && (
                          <Text style={{ fontSize: '14px', color: '#666', display: 'block' }}>
                            {group.description}
                          </Text>
                        )}
                        {group.created_at && (
                          <Text style={{ fontSize: '12px', color: '#999' }}>
                            Created: {DateFormat(group.created_at)}
                          </Text>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </div>
        </div>
      </Sidebar>
    </>
  );
}